import { Link } from '@tanstack/react-router';
import { format, subMonths } from 'date-fns';

import { useGetParentById } from '~entities/parent/api/get-parent-by-id';
import { useGetParentPaymentHistory } from '~entities/parent/api/get-parent-payment-history';
import { useGetParentSavedPaymentMethods } from '~entities/payment/api/get-parent-saved-payment-methods';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import {
  type FamilyPaymentLine,
  formatSavedPaymentMethod,
  summarizeFamilyPayments,
} from '~features/students/ui/family-payment-overview';
import { collectUnsignedParentPolicies } from '~features/students/ui/parent-policies.helpers';

import { useGetCurrency } from '~shared/api/currency-api';

function ChargeRow({
  line,
  money,
  showDueDate,
}: {
  line: FamilyPaymentLine;
  money: (amount: number) => string;
  showDueDate?: boolean;
}) {
  return (
    <li className='flex flex-wrap items-center justify-between gap-2 text-sm'>
      <span>
        {line.studentName} · {line.chargeLabel} · {line.status}
        {showDueDate && line.dueDate ? ` · due ${line.dueDate.slice(0, 10)}` : ''}
      </span>
      <span className='flex items-center gap-3'>
        <span>{money(line.amount)}</span>
        {line.studentId ? (
          <Link
            to='/students/$studentId/new-payments'
            params={{ studentId: line.studentId }}
            className='text-custom-secondary-1 underline'
          >
            Open
          </Link>
        ) : null}
      </span>
    </li>
  );
}

export function FamilyPaymentOverviewCard({ parentId }: { parentId: string }) {
  const studio = useCurrentStudio();
  const { data: parent } = useGetParentById(parentId);
  const { data: currency } = useGetCurrency();
  const today = new Date();
  const { data: history, isLoading, isError } = useGetParentPaymentHistory({
    parentId,
    startDate: format(subMonths(today, 6), 'yyyy-MM-dd'),
    endDate: format(
      new Date(today.getFullYear(), today.getMonth() + 2, 1),
      'yyyy-MM-dd',
    ),
    dateField: 'dueDate',
  });
  const { data: methods } = useGetParentSavedPaymentMethods({
    email: parent?.email,
    locationId: studio?.locationId,
    parentId,
  });

  // Parent.policies is often `{ loadState, agreedPolicies }` (object), not an
  // array — calling .flatMap on it crashed Family Hub (prod Jean Laubinger).
  const unsignedPolicies = collectUnsignedParentPolicies(parent?.policies);
  const summary = summarizeFamilyPayments(
    Array.isArray(history) ? history : [],
  );
  const defaultMethod =
    methods?.find((method) => method.isDefault) ?? methods?.[0] ?? null;
  const symbol = currency?.symbol ?? '$';
  const money = (amount: number) => `${symbol}${amount.toFixed(2)}`;
  const hasRows =
    summary.open.length > 0 ||
    summary.upcoming.length > 0 ||
    summary.recent.length > 0;

  return (
    <section className='mb-8 rounded-lg border p-4'>
        <div className='mb-3 flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h3 className='text-base font-semibold'>Family payments</h3>
            <p className='text-sm text-muted-foreground'>
              Upcoming and recent charges across linked students.
            </p>
          </div>
          <div className='text-right'>
            <p className='text-sm'>{formatSavedPaymentMethod(defaultMethod)}</p>
            <Link
              to='/parents/$parentId/saved-payment-methods'
              params={{ parentId }}
              className='text-sm text-custom-secondary-1 underline'
            >
              Manage payment methods
            </Link>
          </div>
        </div>
      <p className='mb-3 text-sm'>
        {unsignedPolicies.length === 0
          ? 'No outstanding policies'
          : `Outstanding policies: ${unsignedPolicies
              .map((policy) => policy.title || 'Untitled policy')
              .join(', ')}`}
      </p>
      {isLoading ? <p className='text-sm'>Loading family payments…</p> : null}
      {isError ? (
        <p className='text-sm text-red-600'>
          Family payments could not be loaded.
        </p>
      ) : null}
      {!isLoading && !isError && !hasRows ? (
        <p className='text-sm text-muted-foreground'>
          No open, upcoming, or recent family charges.
        </p>
      ) : null}
      {summary.open.length > 0 ? (
        <div className='mb-4'>
          <p className='mb-2 text-sm font-medium'>Open / overdue</p>
          <ul className='space-y-2'>
            {summary.open.map((line) => (
              <ChargeRow key={line.id} line={line} money={money} showDueDate />
            ))}
          </ul>
        </div>
      ) : null}
      {summary.upcoming.length > 0 ? (
        <div className='mb-4'>
          <p className='mb-2 text-sm font-medium'>Upcoming charges</p>
          <ul className='space-y-2'>
            {summary.upcoming.map((line) => (
              <ChargeRow key={line.id} line={line} money={money} showDueDate />
            ))}
          </ul>
        </div>
      ) : (
        !isLoading &&
        !isError && (
          <p className='mb-4 text-sm text-muted-foreground'>
            No upcoming charges in the next billing window.
          </p>
        )
      )}
      {summary.recent.length > 0 ? (
        <div className='mt-2'>
          <p className='mb-2 text-sm font-medium'>Recent payments</p>
          <ul className='space-y-2'>
            {summary.recent.map((line) => (
              <ChargeRow key={`${line.id}-recent`} line={line} money={money} />
            ))}
          </ul>
        </div>
      ) : null}
      <Link
        to='/parents/transactions-table'
        search={{ parentId }}
        className='mt-4 inline-block text-sm text-custom-secondary-1 underline'
      >
        All family transactions
      </Link>
    </section>
  );
}
